/**
 * Single source of truth for all public-facing contact information.
 *
 * Used by: Footer, ContactPage, AboutPage (founder emails).
 *
 * Every field can be overridden at deploy time via the corresponding
 * VITE_* environment variable in Netlify / .env.local — no code change
 * required to update a phone number, email, or social URL.
 *
 * Adding a new phone number: append one entry to `defaultPhones` below.
 * No changes needed in Footer or any other consumer.
 */

const env =
  typeof import.meta !== 'undefined' && import.meta.env
    ? import.meta.env
    : ({} as Record<string, string | undefined>);

// ── Types ─────────────────────────────────────────────────────────────────────

/** A phone contact entry rendered in the Footer and any future contact surfaces. */
export interface PhoneEntry {
  /** Badge label shown below the number, e.g. "WhatsApp · ES" or "NO" */
  label: string;
  /** Formatted number for display, e.g. "+34 636 023 543" */
  number: string;
  /**
   * Link href:
   *  - WhatsApp numbers → "https://wa.me/<countrycode><number>" (no +, no spaces)
   *  - Regular phones   → "tel:+<countrycode><number>"
   */
  href: string;
  /** When true the Footer renders a chat/WhatsApp icon; otherwise a phone icon. */
  isWhatsApp: boolean;
  /** ISO 3166-1 alpha-2 country code, used for future filtering / display. */
  countryCode: string;
}

// ── Phone entries — add new countries here ────────────────────────────────────

const defaultPhones: PhoneEntry[] = [
  {
    label: 'WhatsApp · ES',
    number: env.VITE_CONTACT_PHONE_ES ?? '+34 636 023 543',
    href: env.VITE_CONTACT_PHONE_ES_HREF ?? 'https://wa.me/34636023543',
    isWhatsApp: true,
    countryCode: 'ES',
  },
  {
    label: 'NO',
    number: env.VITE_CONTACT_PHONE_NO ?? '+47 4670 9988',
    href: env.VITE_CONTACT_PHONE_NO_HREF ?? 'tel:+4746709988',
    isWhatsApp: false,
    countryCode: 'NO',
  },
];

// ── Main config object ─────────────────────────────────────────────────────────

export const publicContact = {
  /** General / public-facing email shown in the footer and contact page. */
  generalEmail: env.VITE_CONTACT_EMAIL ?? 'info@wildy.world',

  /** Business inquiries email shown on the contact page. */
  businessEmail: env.VITE_BUSINESS_EMAIL ?? 'info@wildy.world',

  /** Support email shown on the contact page. */
  supportEmail: env.VITE_SUPPORT_EMAIL ?? 'info@wildy.world',

  /**
   * Phone numbers — rendered in order in the Footer contact card.
   * Extend `defaultPhones` above to add more; zero consumer changes needed.
   */
  phones: defaultPhones,

  /**
   * Postal / office address line. Empty string = hidden in Footer.
   * Set VITE_CONTACT_ADDRESS in Netlify to make it visible.
   */
  address: env.VITE_CONTACT_ADDRESS ?? '',

  /** Google Maps or equivalent URL. Only used when `address` is non-empty. */
  addressHref: env.VITE_CONTACT_ADDRESS_HREF ?? '',

  /**
   * Social media URLs. Empty string = link hidden in Footer.
   * Set the corresponding VITE_SOCIAL_* env var in Netlify to enable each one.
   */
  social: {
    twitter: env.VITE_SOCIAL_TWITTER ?? '',
    instagram: env.VITE_SOCIAL_INSTAGRAM ?? '',
    facebook: env.VITE_SOCIAL_FACEBOOK ?? '',
  },

  /** Founder emails rendered on the About page. */
  founders: {
    marioEmail: env.VITE_FOUNDER_MARIO_EMAIL ?? 'info@wildy.world',
    guillermoEmail: env.VITE_FOUNDER_GUILLERMO_EMAIL ?? 'info@wildy.world',
  },
};

export type PublicContact = typeof publicContact;
